// The rep's day on a map (RepDetailView.trailMap): the GPS trail as a blue line, a start marker and an end ("Now"
// while the day is still running) marker, and the day's visits as numbered pins in their visit-result colors (or, on
// the rep page's Route view, a route's stops, whose legend the page draws).
// MapCanvas has no line layer, so the trail is added on the MapLibre map it hands over (`onMapReady`) and put back
// after every style swap (light/dark); everything else is MapCanvas's own pins and markers.
// The camera fits the day once (on open and when the day changes, as iOS `fitMap` on appear / day change), never on a
// background sync, so a manager panning around is not yanked back every poll.

import type { GeoJSONSource, LineLayerSpecification } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Coordinate } from '@/domain/geo';
import { MapCanvas, type MapCanvasHandle, type MapLibreMap, type MapMarker, type MapPoint } from '@/features/common';
import { LAYER } from '@/features/common/mapLayers';
import { GT_COLORS, useLatest } from '@/ui';
import { coordinatesKey, trailGeoJson, visitPinsKey, type TrailFeatureCollection, type VisitPin } from './repDay';

const TRAIL_SOURCE = 'gt-team-trail-src';
const TRAIL_LAYER = 'gt-team-trail';
/** A day's trail fits a neighbourhood; do not zoom to the doorstep for a single visit. */
const FIT_MAX_ZOOM = 16;
/** MapCanvas's pin and marker layers, bottom first: the trail goes under whichever of them exists. */
const ABOVE_TRAIL: readonly string[] = [LAYER.clusters, LAYER.points, LAYER.pointLabels, LAYER.selected, LAYER.markers];

type TrailData = Parameters<GeoJSONSource['setData']>[0];

const TRAIL_LAYER_SPEC: LineLayerSpecification = {
  id: TRAIL_LAYER,
  type: 'line',
  source: TRAIL_SOURCE,
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: { 'line-color': GT_COLORS.blue, 'line-width': 4, 'line-opacity': 0.9 },
};

/** Adds (or refreshes) the trail under MapCanvas's pins and markers. Throws while a new style is still loading. */
function installTrail(map: MapLibreMap, data: TrailFeatureCollection): void {
  const source = map.getSource<GeoJSONSource>(TRAIL_SOURCE);
  if (source) source.setData(data as TrailData);
  else map.addSource(TRAIL_SOURCE, { type: 'geojson', data: data as TrailData });
  if (map.getLayer(TRAIL_LAYER)) return;
  map.addLayer(TRAIL_LAYER_SPEC, ABOVE_TRAIL.find((id) => map.getLayer(id)));
}

export interface RepTrailMapProps {
  trail: readonly Coordinate[];
  pins: readonly VisitPin[];
  /** Today and the day has not ended: the last point is "Now". */
  live: boolean;
  /**
   * Every visit of the day, located or not (the legend count, as iOS). Null when the pins are not the visits (a route's
   * stops, which have a legend of their own): the legend leaves the entry out.
   */
  visitCount: number | null;
  /** Changes when another day is shown (its `YYYY-MM-DD`): the camera fits that day's trail and pins again. */
  fitKey: string;
  onPinClick?: (pin: VisitPin) => void;
  ariaLabel: string;
  className?: string;
}

export function RepTrailMap({ trail: trailIn, pins: pinsIn, live, visitCount, fitKey, onPinClick, ariaLabel, className }: RepTrailMapProps) {
  const handle = useRef<MapCanvasHandle>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  // Same content → same arrays (see coordinatesKey): the sources below are only re-uploaded when the drawing changes.
  const trailKey = useMemo(() => coordinatesKey(trailIn), [trailIn]);
  const pinsKey = useMemo(() => visitPinsKey(pinsIn), [pinsIn]);
  // Keyed on the content on purpose: the first array with that content is kept.
  const trail = useMemo(() => trailIn, [trailKey]);
  const pins = useMemo(() => pinsIn, [pinsKey]);

  const data = useMemo(() => trailGeoJson(trail), [trail]);
  const latestData = useLatest(data);

  const points = useMemo<MapPoint[]>(
    () => pins.map((p) => ({ id: p.id, coordinate: p.coordinate, color: p.color, label: p.label })),
    [pins],
  );
  const markers = useMemo<MapMarker[]>(() => {
    const out: MapMarker[] = [];
    const first = trail[0];
    const last = trail[trail.length - 1];
    if (first) out.push({ id: 'start', coordinate: first, color: GT_COLORS.green, label: 'S', title: 'Start' });
    if (trail.length >= 2 && last) {
      out.push(
        live
          ? { id: 'end', coordinate: last, color: GT_COLORS.blue, title: 'Now' }
          : { id: 'end', coordinate: last, color: GT_COLORS.red, label: 'E', title: 'End' },
      );
    }
    return out;
  }, [trail, live]);
  const coords = useMemo(() => [...trail, ...pins.map((p) => p.coordinate)], [trail, pins]);
  const latestCoords = useLatest(coords);

  const fit = useCallback(
    (animate: boolean) => {
      if (latestCoords.current.length > 0) handle.current?.fitBounds(latestCoords.current, { maxZoom: FIT_MAX_ZOOM, animate });
    },
    [latestCoords],
  );

  const onMapReady = useCallback(
    (map: MapLibreMap) => {
      mapRef.current = map;
      const reinstall = () => {
        try {
          installTrail(map, latestData.current);
        } catch {
          // The next style.load puts it back.
        }
      };
      reinstall();
      // Removed with the map itself (MapCanvas calls map.remove() on unmount).
      map.on('style.load', reinstall);
      fit(false);
    },
    [latestData, fit],
  );

  // New breadcrumbs (or another day): new line data, no camera move.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      installTrail(map, data);
    } catch {
      // Style swap in progress; its style.load handler installs the latest data.
    }
  }, [data]);

  // Another day: fit it (animated once the map is up; onMapReady does the first fit).
  useEffect(() => {
    if (mapRef.current) fit(true);
  }, [fitKey, fit]);

  const pinsById = useMemo(() => new Map(pins.map((p) => [p.id.toLowerCase(), p])), [pins]);

  return (
    <MapCanvas
      ref={handle}
      ariaLabel={ariaLabel}
      points={points}
      cluster={false}
      markers={markers}
      fitOnFirstData={false}
      padding={40}
      onMapReady={onMapReady}
      onPointClick={
        onPinClick
          ? (id) => {
              const pin = pinsById.get(id.toLowerCase());
              if (pin) onPinClick(pin);
            }
          : undefined
      }
      className={className}
    >
      <div className="pointer-events-none absolute bottom-2 left-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full bg-surface/90 px-3 py-1.5 text-[11px] text-fg shadow-card backdrop-blur">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-1 w-4 rounded-full" style={{ backgroundColor: GT_COLORS.blue }} />
          Trail
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: GT_COLORS.green }} />
          Start
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: live ? GT_COLORS.blue : GT_COLORS.red }} />
          {live ? 'Now' : 'End'}
        </span>
        {visitCount !== null && (
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: GT_COLORS.orange }} />
            Visits ({visitCount})
          </span>
        )}
      </div>
    </MapCanvas>
  );
}
